import apiClient from '../client';

// Types
export interface DataRetentionPolicy {
  id: number;
  policy_id: string;
  policy_name: string;
  data_category: string;
  description?: string;
  retention_period_days: number;
  archive_after_days?: number;
  purge_after_days?: number;
  legal_basis?: string;
  compliance_frameworks?: string[];
  geographic_scope?: string;
  auto_archive_enabled: boolean;
  auto_purge_enabled: boolean;
  notification_days_before?: number;
  enabled: boolean;
  last_processed_at?: string;
  next_processing_at?: string;
  total_records_processed: number;
  records_archived: number;
  records_purged: number;
  created_at: string;
}

export interface RetentionDashboard {
  success: boolean;
  dashboard: {
    policies: {
      total: number;
      enabled: number;
      disabled: number;
    };
    data_summary: {
      total_records: number;
      records_to_archive: number;
      records_to_purge: number;
      archived_records: number;
      purged_records: number;
    };
    compliance_status: {
      compliant_policies: number;
      non_compliant_policies: number;
      compliance_score: number;
    };
    processing_stats: {
      last_run: string;
      next_run: string;
      records_processed_today: number;
      records_archived_today: number;
      records_purged_today: number;
    };
  };
}

export interface RetentionReport {
  report_id: string;
  report_type: string;
  generated_at: string;
  period_start: string;
  period_end: string;
  summary: {
    total_policies: number;
    records_processed: number;
    records_archived: number;
    records_purged: number;
    compliance_score: number;
  };
  details: any;
}

// API Functions
export const dataRetentionService = {
  // Get retention dashboard
  getDashboard: async (): Promise<RetentionDashboard> => {
    const response = await apiClient.get('/data-retention/dashboard');
    return response.data;
  },

  // Get retention policies
  getPolicies: async (params?: {
    data_category?: string;
    enabled?: boolean;
    skip?: number;
    limit?: number;
  }): Promise<{ policies: DataRetentionPolicy[]; total: number }> => {
    const response = await apiClient.get('/data-retention/policies', { params });
    // Map backend response { retention_policies: [...], total_policies: ... } to frontend expectation
    return {
      policies: response.data.retention_policies || [],
      total: response.data.total_policies || 0
    };
  },

  // Get policy by ID
  getPolicy: async (policyId: string): Promise<{ success: boolean; policy: DataRetentionPolicy }> => {
    const response = await apiClient.get(`/data-retention/policies/${policyId}`);
    return response.data;
  },

  // Create retention policy
  createPolicy: async (data: Partial<DataRetentionPolicy>): Promise<{
    success: boolean;
    policy: DataRetentionPolicy;
  }> => {
    const response = await apiClient.post('/data-retention/policies', data);
    // Backend returns { success, policy_id, policy_details }
    // We need to shape it as { success, policy: { ...details, id: policy_id } }
    const { policy_id, policy_details } = response.data;
    return {
      success: response.data.success,
      policy: {
        ...policy_details,
        id: policy_id,
        policy_id: policy_id // Ensure compatibility
      } as DataRetentionPolicy
    };
  },

  // Update retention policy
  updatePolicy: async (policyId: string, data: Partial<DataRetentionPolicy>): Promise<{
    success: boolean;
    policy: DataRetentionPolicy;
  }> => {
    const response = await apiClient.put(`/data-retention/policies/${policyId}`, data);
    return response.data;
  },

  // Delete retention policy
  deletePolicy: async (policyId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete(`/data-retention/policies/${policyId}`);
    return response.data;
  },

  // Enable/disable policy
  togglePolicy: async (policyId: string, enabled: boolean): Promise<{
    success: boolean;
    policy: DataRetentionPolicy;
  }> => {
    const response = await apiClient.post(`/data-retention/policies/${policyId}/toggle`, { enabled });
    return response.data;
  },

  // Run policy now
  runPolicy: async (policyId: string, dryRun: boolean = false): Promise<{
    success: boolean;
    execution: {
      execution_id: string;
      records_processed: number;
      records_archived: number;
      records_purged: number;
      duration_seconds: number;
    };
  }> => {
    const response = await apiClient.post(`/data-retention/policies/${policyId}/run`, { dry_run: dryRun });
    return response.data;
  },

  // Get data categories
  getCategories: async (): Promise<{ success: boolean; categories: string[] }> => {
    const response = await apiClient.get('/data-retention/categories');
    return response.data;
  },

  // Get records for review
  getRecordsForReview: async (params?: {
    data_category?: string;
    action?: string; // 'archive' or 'purge'
    days_until_action?: number;
    skip?: number;
    limit?: number;
  }): Promise<{
    success: boolean;
    records: Array<{
      record_id: string;
      data_category: string;
      created_at: string;
      action_due_date: string;
      days_until_action: number;
      action_type: string;
    }>;
    total: number;
  }> => {
    const response = await apiClient.get('/data-retention/records-for-review', { params });
    return response.data;
  },

  // Generate retention report
  generateReport: async (reportType: string, params?: {
    start_date?: string;
    end_date?: string;
    data_category?: string;
  }): Promise<{ success: boolean; report: RetentionReport }> => {
    const response = await apiClient.post('/data-retention/reports/generate', {
      report_type: reportType,
      ...params,
    });
    return response.data;
  },

  // Get retention statistics
  getStatistics: async (days: number = 30): Promise<{
    success: boolean;
    statistics: {
      period_days: number;
      records_processed: number;
      records_archived: number;
      records_purged: number;
      by_category: { [key: string]: { processed: number; archived: number; purged: number } };
      trend: Array<{ date: string; processed: number; archived: number; purged: number }>;
    };
  }> => {
    const response = await apiClient.get(`/data-retention/statistics?days=${days}`);
    return response.data;
  },

  // Get compliance status
  getComplianceStatus: async (): Promise<{
    success: boolean;
    compliance: {
      overall_score: number;
      status: string;
      policies_compliant: number;
      policies_non_compliant: number;
      issues: Array<{
        policy_id: string;
        policy_name: string;
        issue: string;
        severity: string;
      }>;
      recommendations: string[];
    };
  }> => {
    const response = await apiClient.get('/data-retention/compliance-status');
    return response.data;
  },

  // Archive records manually
  archiveRecords: async (data: {
    data_category: string;
    record_ids?: string[];
    older_than_days?: number;
  }): Promise<{
    success: boolean;
    archived_count: number;
    execution_id: string;
  }> => {
    const response = await apiClient.post('/data-retention/archive', data);
    return response.data;
  },

  // Purge records manually
  purgeRecords: async (data: {
    data_category: string;
    record_ids?: string[];
    older_than_days?: number;
    confirm: boolean;
  }): Promise<{
    success: boolean;
    purged_count: number;
    execution_id: string;
  }> => {
    const response = await apiClient.post('/data-retention/purge', data);
    return response.data;
  },
};

export default dataRetentionService;

